/**
 * ForwardLookingPanel — surfaces the proactive/foresight metrics
 * (cash runway, renewal cliff, concentration risk, pipeline coverage)
 * as a single set of tap-through tiles in the Command Center sidebar.
 *
 * Each tile is fully clickable and deep-links to its dedicated hub +
 * detail view via the shared focus query convention.
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { useBasePath, joinNav } from '@/lib/config/nav-helpers';
import { useMe } from '@/lib/hooks/use-me';
import { getCashRunway } from '@/lib/cash-runway';
import { getRenewalCliff } from '@/lib/renewal-cliff';
import { getConcentrationRisk } from '@/lib/concentration-risk';
import { getPipelineCoverage } from '@/lib/pipeline-coverage';
import { notConnectedLine, sees, type Need } from '@/lib/coverage';
import { DataCard, StatusBadge, QueryError } from '../ui';

function fmt(cents: number): string {
  const n = Math.round(cents / 100);
  if (n >= 1_000_000) return '$' + (n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1) + 'M';
  if (n >= 1_000) return '$' + Math.round(n / 1_000) + 'k';
  return '$' + n;
}

export function ForwardLookingPanel() {
  const navigate = useNavigate();
  const basePath = useBasePath();
  const me = useMe();

  const runway = useQuery({ queryKey: ['cash-runway'], queryFn: getCashRunway, staleTime: 5 * 60_000 });
  const renewal = useQuery({ queryKey: ['renewal-cliff'], queryFn: getRenewalCliff, staleTime: 10 * 60_000 });
  const concentration = useQuery({ queryKey: ['concentration-risk'], queryFn: getConcentrationRisk, staleTime: 15 * 60_000 });
  const coverage = useQuery({ queryKey: ['pipeline-coverage'], queryFn: getPipelineCoverage, staleTime: 5 * 60_000 });

  // Coverage over the revenue gap needs a CRM (pipeline) and accounting/payments (what is collected).
  const coverageGap = coverage.data ? (['crm', 'money'] as const).find((n) => !sees(coverage.data.coverage, n)) ?? null : null;
  // Each of the other tiles needs one source; no `coverage` yet (loading, or a failed read) claims nothing either way.
  const unseen = (need: Need, c: { coverage?: Parameters<typeof sees>[0] } | undefined) => !!c && (!c.coverage || !sees(c.coverage, need));
  const runwayGap = unseen('money', runway.data) && runway.data?.weeks == null;
  const renewalGap = unseen('crm', renewal.data) && renewal.data?.summary == null;
  const concentrationGap = unseen('money', concentration.data) && concentration.data?.topShare == null;

  return (
    <DataCard title="Forward Look" subtitle="Where you're heading, not just where you've been">
      {(runway.isError || concentration.isError || coverage.isError) && (
        <QueryError
          error={runway.error ?? concentration.error ?? coverage.error}
          onRetry={() => {
            if (runway.isError) runway.refetch();
            if (concentration.isError) concentration.refetch();
            if (coverage.isError) coverage.refetch();
          }}
          compact
        />
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Cash Runway */}
        <Tile
          label="Cash Runway"
          value={
            runway.data?.weeksUntilCrunch
              ? `${runway.data.weeksUntilCrunch}w`
              : runway.data && (runway.data.totalInflow13wCents ?? 0) > 0
                ? fmt(runway.data.totalInflow13wCents!)
                : '—'
          }
          sub={
            runway.data?.weeksUntilCrunch
              ? 'until cash crunch'
              : runway.data && (runway.data.totalInflow13wCents ?? 0) > 0
                ? 'expected inflow / 13w'
                : runwayGap ? notConnectedLine('money') : 'no forecast yet'
          }
          severity={runway.data?.weeksUntilCrunch && runway.data.weeksUntilCrunch <= 4 ? 'critical'
            : runway.data?.weeksUntilCrunch && runway.data.weeksUntilCrunch <= 8 ? 'warning'
              : 'neutral'}
          onClick={() => navigate(joinNav(basePath, '/revenue?focus=cash-runway'))}
        />

        {/* Pipeline Coverage */}
        <Tile
          label="Pipeline Coverage"
          value={coverage.data?.coverageRaw != null && coverage.data.status && coverage.data.status !== 'unknown' ? `${coverage.data.coverageRaw.toFixed(1)}×` : '—'}
          sub={coverage.data?.status === 'healthy' ? 'on track vs 3× target'
            : coverage.data?.status === 'tight' ? 'tight — need most deals'
              : coverage.data?.status === 'at-risk' ? 'below benchmark'
                : coverageGap ? notConnectedLine(coverageGap)
                  : 'no goal or pipeline to compare yet'}
          severity={coverage.data?.status === 'at-risk' ? 'critical'
            : coverage.data?.status === 'tight' ? 'warning'
              : 'neutral'}
          onClick={() => navigate(joinNav(basePath, '/pipeline?focus=coverage'))}
        />

        {/* Renewal Cliff */}
        <Tile
          label="Renewal Cliff"
          value={renewal.data?.summary ? String(renewal.data.summary.overdue + renewal.data.summary.next30Days) : '—'}
          sub={renewal.data?.summary && (renewal.data.summary.overdue + renewal.data.summary.next30Days) > 0
            ? `${renewal.data.summary.overdue} overdue · 30d window`
            : renewal.data?.summary ? 'no renewals at risk'
              : renewalGap ? notConnectedLine('crm') : 'renewals unavailable'}
          severity={renewal.data?.summary && renewal.data.summary.overdue > 0 ? 'critical'
            : renewal.data?.summary && renewal.data.summary.next30Days > 0 ? 'warning'
              : 'neutral'}
          // Renewals live on the Contracts hub; Clients has no renewal view to focus.
          onClick={() => navigate(joinNav(basePath, '/contracts'))}
        />

        {/* Concentration Risk */}
        <Tile
          label="Concentration"
          value={concentration.data && concentration.data.topShare != null && concentration.data.source !== 'none'
            ? `${concentration.data.topShare.toFixed(0)}%`
            : '—'}
          sub={concentration.data?.customers && concentration.data.customers.length > 0
            ? `top client of ${concentration.data.customers.length}`
            : concentrationGap ? notConnectedLine('money') : 'connect billing to compute'}
          severity={concentration.data?.severity === 'critical' ? 'critical'
            : concentration.data?.severity === 'high' ? 'warning'
              : 'neutral'}
          onClick={() => navigate(joinNav(basePath, '/revenue?focus=concentration'))}
        />
      </div>

      {/* Narrative line — first non-empty narrative wins */}
      {(runway.data?.narrative || coverage.data?.narrative) && (
        <p className="mt-3 text-2xs leading-relaxed text-ink-muted">
          {runway.data?.weeksUntilCrunch && runway.data.weeksUntilCrunch <= 8
            ? (runway.data.narrative ?? '')
            : coverage.data?.status === 'at-risk'
              ? (coverage.data.narrative ?? '')
              : concentration.data?.severity === 'critical' || concentration.data?.severity === 'high'
                ? (concentration.data.narrative ?? '')
                : (runway.data?.narrative ?? coverage.data?.narrative ?? '')}
        </p>
      )}

      {/* Department goals — only shown if the user is a member of at least one dept */}
      {me.departments.length > 0 && (
        <div className="mt-4 border-t border-border/40 pt-3">
          <div className="mb-2 text-2xs font-semibold uppercase tracking-wider text-ink-dim">Your departments</div>
          <div className="space-y-1.5">
            {me.departments.map(d => (
              <div
                key={d.id}
                onClick={() => navigate(joinNav(basePath, '/settings'))}
                className="flex items-center justify-between cursor-pointer rounded-md px-2 py-1.5 hover:bg-surface-2/60 transition-colors"
                style={d.color ? { borderLeft: `3px solid ${d.color}`, paddingLeft: '8px' } : undefined}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-2xs font-medium text-ink truncate">{d.name}</span>
                    {d.myRole === 'head' && (
                      <span className="rounded-full bg-primary-soft px-1.5 py-0.5 text-2xs font-semibold uppercase tracking-wider text-primary-bright">head</span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xs text-ink-dim">Goal</div>
                  <div className="font-mono text-xs font-semibold tabular-nums text-ink">
                    {d.quarterlyRevenueGoalCents != null ? fmt(d.quarterlyRevenueGoalCents) : '—'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </DataCard>
  );
}

function Tile({
  label, value, sub, severity, onClick,
}: {
  label: string;
  value: string;
  sub: string;
  severity: 'critical' | 'warning' | 'neutral';
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      role="button"
      className={clsx(
        'cursor-pointer rounded-xl border px-3 py-2.5 transition-all hover:-translate-y-0.5 hover:shadow-lift',
        severity === 'critical' ? 'border-danger/30 bg-danger/3 hover:border-danger/50'
          : severity === 'warning' ? 'border-warn/30 bg-warn/3 hover:border-warn/50'
            : 'border-border hover:border-border-strong',
      )}
    >
      <div className="flex items-center justify-between mb-0.5">
        <span className="text-2xs font-semibold uppercase tracking-wider text-ink-dim">{label}</span>
        {severity !== 'neutral' && (
          <StatusBadge status={severity === 'critical' ? 'critical' : 'warning'} label={severity === 'critical' ? 'urgent' : 'watch'} size="xs" pulse={severity === 'critical'} />
        )}
      </div>
      <div className={clsx(
        'font-mono text-lg font-semibold tabular-nums',
        severity === 'critical' ? 'text-danger' : severity === 'warning' ? 'text-warn' : 'text-ink',
      )}>
        {value}
      </div>
      <div className="mt-0.5 text-2xs text-ink-dim leading-tight">{sub}</div>
    </div>
  );
}
